import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";

interface Props {
  Icon: React.ElementType;
  statisticTitle: string;
  statisticFigure: number | null;
}

const getColor = (title: string): string => {
  switch (title) {
    case "Total books":
      return "#007AFF";
    case "Books completed":
      return "#4BA663";
    case "Books in Progress":
      return "#D97706";
    case "Books Delivered":
      return "#5856D6";
    case "Monthly Progress":
      return "#800080";
    default:
      return "#4285F4";
  }
};

export const StatisticsCard: React.FC<Props> = ({
  Icon,
  statisticFigure,
  statisticTitle,
}) => {
  const value = statisticFigure ?? 0;
  const color = getColor(statisticTitle);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = value / (duration / 16) || value;

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          height: "100%",
          borderRadius: 2,
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          border: "1px solid",
          borderColor: `${color}30`,
          position: "relative",
          overflow: "hidden",
          minWidth: 0,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 8px 16px ${color}25`,
            borderColor: color,
            "& .metric-icon": {
              transform: "rotate(10deg) scale(1.1)",
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: -30,
            right: -30,
            width: "100px",
            height: "100px",
            background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
            borderRadius: "50%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            {statisticTitle}
          </Typography>

          <Box
            className="metric-icon"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 8px ${color}40`,
              transition: "transform 0.4s ease",
              color: "white",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color,
            fontSize: { xs: "1.6rem", sm: "2rem", md: "2.5rem" },
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {displayValue.toLocaleString()}
        </Typography>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${color} 0%, ${color}60 100%)`,
            opacity: 0.6,
          }}
        />
      </Paper>
    </Grid>
  );
};